// One audio track for the recording, mixed from the mic and every remote peer.
//
// This is the piece the old implementation had no answer for. It recorded
// `getDisplayMedia({audio: true})`, which captures *display* audio and never the
// microphone — and on macOS window/screen capture, usually nothing at all. So a
// local recording either had no sound or had everyone except the person who made
// it. Neither is a recording anyone can use.
//
// Mixing in WebAudio is cheap (audio is a rounding error next to video encode)
// and gives an explicit, correct result: every voice in the room, including
// your own.

export type AudioMixer = {
  /** The single mixed track to hand to MediaRecorder. */
  readonly track: MediaStreamTrack
  /**
   * Reconcile the mix against the streams that should be in it. Called whenever
   * peers join or leave, so a recording started before someone arrives still
   * captures them.
   */
  setSources(streams: MediaStream[]): void
  close(): void
}

type WebkitWindow = Window & { webkitAudioContext?: typeof AudioContext }

export function createAudioMixer(streams: MediaStream[]): AudioMixer | null {
  const Ctor = window.AudioContext ?? (window as WebkitWindow).webkitAudioContext
  if (!Ctor) return null

  const ctx = new Ctor()
  const destination = ctx.createMediaStreamDestination()
  // Keyed by stream id, so re-running setSources with the same peers is a no-op
  // rather than tearing the graph down and rebuilding it.
  const sources = new Map<string, MediaStreamAudioSourceNode>()

  const connect = (stream: MediaStream) => {
    if (sources.has(stream.id)) return
    const audio = stream.getAudioTracks()
    if (audio.length === 0) return
    try {
      // Wrap the audio track in a fresh MediaStream rather than handing over the
      // peer's own stream object. That same object is the srcObject of the
      // hidden <audio> element that plays this peer, and of the tile's <video>;
      // giving it to createMediaStreamSource makes the AudioContext and those
      // elements contend for one stream, which in Chrome can silence playback.
      // A wrapper shares the track without touching who owns the stream.
      const node = ctx.createMediaStreamSource(new MediaStream([audio[0]]))
      node.connect(destination)
      sources.set(stream.id, node)
    } catch {
      // A stream whose track ended between the check and here — skip it; the
      // next reconcile picks up its replacement.
    }
  }

  const disconnect = (id: string) => {
    const node = sources.get(id)
    if (!node) return
    try { node.disconnect() } catch { /* already detached */ }
    sources.delete(id)
  }

  streams.forEach(connect)
  // Resume HERE, not only in setSources. Chrome creates an AudioContext
  // suspended, and a suspended graph feeds the recorder silence. Relying on the
  // first setSources() to wake it left a race: if nothing changed in the meeting
  // straight after the user hit record, the file was silent. That is the
  // "sometimes there is no audio" report.
  if (ctx.state === 'suspended') void ctx.resume()

  return {
    track: destination.stream.getAudioTracks()[0],

    setSources(next) {
      const wanted = new Set(next.filter((s) => s.getAudioTracks().length > 0).map((s) => s.id))
      for (const id of [...sources.keys()]) {
        if (!wanted.has(id)) disconnect(id)
      }
      next.forEach(connect)
      // Also re-checked here: a backgrounded tab can suspend the context long
      // after it was started.
      if (ctx.state === 'suspended') void ctx.resume()
    },

    close() {
      for (const id of [...sources.keys()]) disconnect(id)
      destination.disconnect()
      void ctx.close()
    },
  }
}
